import axios from 'axios';
import { LocalStorageKeys } from 'src/viewsTypes'; // Assuming this enum exists and is relevant

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000'; // Fallback for local dev

// Define the expected shape of user data from the backend
interface UserData {
  id: string;
  email: string;
  name: string;
  firstName: string;
  lastName: string;
  inboxId: string;
  isNewUser: boolean;
}

// Define the expected shape of the auth response from the backend
interface AuthResponse {
  token: string;
  user: UserData;
}

/**
 * Gets the Google OAuth URL from the backend.
 */
const getAuthUrl = async (): Promise<string> => {
  try {
    const response = await axios.get<{ url: string }>(
      `${API_URL}/api/gmail/auth`,
    );
    if (response.data && response.data.url) {
      return response.data.url;
    }
    throw new Error('Auth URL not received from backend');
  } catch (error) {
    console.error('Failed to get auth URL:', error);
    throw error; // Re-throw to be handled by the caller
  }
};

/**
 * Handles the OAuth callback by sending the code (or other params)
 * to the backend to exchange for a token and user data.
 *
 * @param searchParams URLSearchParams from the callback URL
 * @returns Promise resolving with AuthResponse (token and user data)
 */
const handleAuthCallback = async (
  searchParams: URLSearchParams,
): Promise<AuthResponse> => {
  // Check if we're handling a direct OAuth redirect (token and user in params)
  const token = searchParams.get('token');
  const userParam = searchParams.get('user');

  if (token && userParam) {
    try {
      const user = JSON.parse(userParam);
      return { token, user };
    } catch (error) {
      console.error('Failed to parse user data from URL:', error);
      throw new Error('Invalid user data in callback URL');
    }
  }

  // If not a direct OAuth redirect, handle as a code exchange
  const code = searchParams.get('code');
  if (!code) {
    console.error('No code found in URL:', window.location.href);
    throw new Error('Authorization code not found in callback URL');
  }

  try {
    console.log('Sending code to backend:', code.substring(0, 10) + '...');

    // Send the code to your backend callback endpoint
    const response = await axios.post<AuthResponse>(
      `${API_URL}/api/gmail/callback`,
      { code },
    );

    console.log('Response status:', response.status);
    console.log('Response headers:', response.headers);
    console.log('Response data:', JSON.stringify(response.data, null, 2));

    // Validate the response structure
    if (response.data && response.data.token && response.data.user) {
      return response.data;
    }

    console.error('Invalid response structure:', response.data);
    throw new Error('Invalid response structure from auth callback endpoint');
  } catch (error) {
    console.error('Failed to handle auth callback:', error);

    // More detailed error logging
    if (axios.isAxiosError(error)) {
      console.error('Response status:', error.response?.status);
      console.error('Response data:', error.response?.data);
    }

    throw error; // Re-throw
  }
};

/**
 * Stores the authentication token.
 * @param token The token string to store.
 */
const storeToken = (token: string): void => {
  // Using localStorage for simplicity, consider httpOnly cookies for better security
  localStorage.setItem(LocalStorageKeys.AuthToken, token); // Define AuthToken in your enum
};

/**
 * Retrieves the stored authentication token.
 * @returns The token string or null if not found.
 */
const getToken = (): string | null => {
  return localStorage.getItem(LocalStorageKeys.AuthToken);
};

/**
 * Clears the stored authentication token.
 */
const clearToken = (): void => {
  localStorage.removeItem(LocalStorageKeys.AuthToken);
};

/**
 * Fetches the user profile using the stored authentication token.
 * This makes a request to the /api/users/me endpoint which validates
 * the token and returns the current user data.
 */
const fetchUserProfile = async (): Promise<UserData | null> => {
  const token = getToken();
  if (!token) return null;

  try {
    console.log('Fetching user profile with token');

    // Request user data from the backend using the token
    const response = await axios.get<UserData>(`${API_URL}/api/users/me`, {
      headers: { Authorization: `Bearer ${token}` },
    });

    console.log('User profile response:', response.data);

    // Validate that we have the required fields
    if (!response.data || !response.data.email) {
      console.error('Invalid user profile data received:', response.data);
      return null;
    }

    // Ensure we have firstName and lastName - use email as fallback if they're missing
    const userData: UserData = {
      ...response.data,
      firstName: response.data.firstName || response.data.email.split('@')[0],
      lastName: response.data.lastName || '',
    };

    return userData;
  } catch (error) {
    console.error('Failed to fetch user profile:', error);

    // Detailed error logging
    if (axios.isAxiosError(error)) {
      console.error('Response status:', error.response?.status);
      console.error('Response data:', error.response?.data);

      // Handle specific errors
      if (error.response?.status === 401) {
        console.error('Token is unauthorized or expired');
        clearToken(); // Clear expired token
      } else if (error.response?.status === 404) {
        console.error('User profile endpoint not found');
      }
    }

    return null;
  }
};

export const authService = {
  getAuthUrl,
  handleAuthCallback,
  storeToken,
  getToken,
  clearToken,
  fetchUserProfile,
};

// Define LocalStorageKeys.AuthToken if it doesn't exist
// You might need to update src/viewsTypes.ts
/*
export enum LocalStorageKeys {
  UserId = 'userId',
  InboxId = 'inboxId',
  Email = 'email',
  AuthToken = 'authToken', // Add this line
}
*/
